import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button, Card, CheckboxGroup, IconButton, Input, RadioGroup, ScrollArea, Typography } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useSurveyShownBi } from '../../../../lib/use-survey-shown-bi';
import { CHAT_HOME_BI } from '../message-utils';
import {
  optionIndex,
  questionIndex,
  SURVEY_BI,
  SURVEY_COMPONENT,
  SURVEY_STEP_TARGET,
  surveyProgress,
  type SurveyDismissReason,
  type SurveyPageTrigger,
} from '../../../../lib/survey-bi';
import { messageDir } from '../../../../lib/message-direction';
import {
  buildSurveyAnswer,
  composeSurveyMessageText,
  showsPictures,
  type MessageSurvey,
  type SurveyAnswer,
  type SurveyDraft,
  type SurveyQuestion,
} from '../../../../lib/message-survey';
import styles from './SurveyWidget.module.css';

/**
 * The pause between a pick and the page turning.
 *
 * It is NOT a transition duration, which is what 180ms was and why the review said "it moves to the
 * next question too fast — I don't manage to see what I chose". The beat has to cover a READING
 * action, not a repaint: the eye leaves the option it just tapped, finds the radio, confirms the dot
 * is in the right row, and only then is the page free to turn. Under about half a second the page is
 * already gone when the eye arrives, and the reader is left holding a question they are no longer
 * sure they answered — on a card whose whole point is that they never have to check.
 *
 * Long enough to read the pick back, still short enough that the turn reads as a CONSEQUENCE of the
 * tap rather than a timer the reader is waiting out. Any manual move (Next, the pager, Skip, another
 * pick) voids it, so nobody who is faster than this has to sit through it.
 */
const AUTO_ADVANCE_MS = 900;

/**
 * The "Other" row's option id. It is NOT one of the survey's options — the harness mints `opt_n` —
 * and it deliberately never reaches the wire: `buildSurveyAnswer` keeps only ids the question
 * actually offers, so picking Other contributes the TYPED TEXT and nothing else. That is the honest
 * answer: "other" is not a choice the asker offered, it is the reader declining all of them.
 */
const OTHER_OPTION_ID = '__other';

// The paginated survey card (Figma "survey-card-mobile") — one question per page. An OPEN survey
// stands in the composer's place (MessagesPanel); the transcript row keeps the settled prompt list
// below the assistant body, in the slot `choices` chips occupy. Header = the current question's prompt +
// a close (X); option rows are the DS Radio/Checkbox group (label + optional description); an
// always-editable free-text field ENDS every question's answers — `allowFreeText` is forced true for
// every question (harness offer-survey-tool, and lib/message-survey reads past a stale stored one), so
// the reader can always write their own answer instead of picking; the footer pages
// through the questions and submits ONE `surveyAnswer` over every page's picks. Picks and typed text
// persist across pages until submit. Skip and X dismiss the whole survey unanswered.
//
// Once it SETTLES — answered, skipped, or a read-only surface — the card is replaced by the record of
// what was ASKED: one line per question prompt, in the chat's own type, with no card, no options and
// no bubble. The ANSWERS are deliberately not repeated here; they already ride the user's own message
// (`composeSurveyMessageText`), which is the turn that carries them, and drawing them twice makes the
// transcript read as two answers to one ask. Answered-ness comes off the transcript (the USER row's
// `surveyAnswer`, passed as `submittedAnswer`), so it survives a reload; a skip settles for the
// session only, which is deliberate — an unanswered survey is still answerable when the chat reopens.
export function SurveyWidget({
  survey,
  submittedAnswer,
  onSubmit,
  onDismiss,
  onReopen,
  conversationId,
  messageId,
  surfaceVisible,
  inComposer,
}: {
  survey: MessageSurvey;
  submittedAnswer?: SurveyAnswer;
  // Submit the filled-in survey as a normal user turn. Absent (read-only surface, locked channel) =>
  // there is nothing to fill in, so it settles to the prompt list like an answered one.
  onSubmit?: (answer: SurveyAnswer, text: string) => void;
  // Skip / X / a nothing-answered submit. The COMPOSER-SLOT card hands dismissal UP (the panel owns
  // which survey holds the slot, and giving the slot back to the input is its call); the transcript
  // card keeps its own session-local `dismissed`.
  onDismiss?: () => void;
  // Take the ask BACK after an X. Passed only when this settled prompt list is still the conversation's
  // current ask (nothing said past it) — see `reopenableSurveyAskId` in MessagesPanel — and it is what
  // turns the list from a record into a way back in: with it the prompts are a control, and they are
  // drawn as one. Without it they stay the quiet record they were, because a list that looks clickable
  // and reopens nothing is worse than one that never offered.
  onReopen?: () => void;
  // The conversation this ask belongs to, for BI. Absent on a read-only surface, where nothing here is
  // interactive and there is nothing to report.
  conversationId?: string;
  // The persisted row carrying this ask, for the impression event — the way back to the words, which
  // never ride the event themselves. Absent while the ask is still the turn's early copy, which is
  // exactly when no row carries it yet.
  messageId?: string;
  // Whether the chat surface this card sits on is actually OPEN — false while the floating chat rests
  // (mounted, laid out, `visibility: hidden`). Gates the impression only; nothing else here reads it,
  // because a card behind a closed panel is not being clicked either way. Absent = visible, which is
  // the full-screen surface's case.
  surfaceVisible?: boolean;
  // The card is standing IN PLACE OF the composer rather than inline in the transcript: it owns the
  // composer's own width and bottom padding, so it drops the inline top margin, and once it settles
  // it renders nothing at all — the input comes back in its place.
  inComposer?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('chat-survey');
  const [page, setPage] = useState(0);
  // The impression: this card was put in front of the reader. Only the ANSWERABLE card reports it — the
  // settled record in the transcript is the memory of an offer, not an offer. See use-survey-shown-bi
  // for why it is an `itemSeen` on the card rather than a `loadFinish` on the transcript.
  const offerSeenRef = useSurveyShownBi(survey, conversationId, {
    messageId,
    // Three things have to be true for this card to be an OFFER somebody could see. It has to be the
    // answerable one (`inComposer && onSubmit`) — the settled record in the transcript is the memory of
    // an offer, not an offer. And the surface it sits on has to be OPEN: the floating chat stays mounted
    // and laid out when it is closed, hidden only by `visibility: hidden` (FloatingChat's resting
    // style), which an IntersectionObserver does not see — so without this the card counts as seen
    // behind a shut panel, inflating the denominator of every rate measured against it.
    enabled: surfaceVisible !== false && !!inComposer && !!onSubmit,
  });
  // Every step of the card reports itself — see lib/survey-bi for the vocabulary and for why indices
  // and counts ride these events and the words never do. `ask_id` is on all of them, so a card's steps
  // join into one funnel with the shown event (`useSurveyShownBi`) and the submit the panel fires.
  //
  // One `componentName` for every step — the card is ONE component in the catalogue — and WHICH part of
  // it the finger landed on is the click's `entityType` (`SURVEY_STEP_TARGET`). The step's own long name
  // stays the src:172 `button_name` and rides the generic event as `step`, so nothing that was
  // queryable before stops being queryable.
  //
  // The entity is the ASK, not the conversation: `entity` is serialized as ONE id/type pair, so pairing
  // the conversation's id with `option` would publish an `entities` row claiming the conversation IS an
  // option. `askId` is the thing the target describes a part of, and it joins straight to the
  // impression's `ask_id`; the conversation rides `conversation_id`, on this event and on every other.
  const report = (buttonName: string, fields: Record<string, string>) =>
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: buttonName, ...(conversationId ? { conversation_id: conversationId } : {}) },
      {
        componentName: SURVEY_COMPONENT,
        entity: { id: survey.askId, type: SURVEY_STEP_TARGET[buttonName] ?? buttonName },
        additionalFields: { step: buttonName, ask_id: survey.askId, ...fields },
      },
    );
  // The write-in row reports the FIRST character typed into each question and then goes quiet: the
  // event answers "did the suggestions fail this reader", which is a question about the question, not
  // about how long their sentence was. A per-keystroke event would also be a per-keystroke network call.
  const wroteIn = useRef(new Set<string>());
  // The auto-advance beat (see `setQuestionOptions`). A pending advance is void the moment ANYTHING
  // else happens — another pick, a page the reader turned themselves, a skip — so it is tracked by a
  // token rather than by the timer handle alone. Two review findings came out of getting this wrong:
  // clearing only at the call sites that remembered to (so picking Other after a pick still advanced
  // away and closed the field it had just opened), and guarding only on "am I still on the page I was
  // scheduled from" (so Next-then-Previous inside the beat made the guard true again and the advance
  // fired against the reader's last action). A monotonic token answers the real question — has
  // anything happened since I was scheduled — which neither of those did.
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const advanceToken = useRef(0);
  const cancelAdvance = () => {
    clearTimeout(advanceTimer.current);
    advanceTimer.current = undefined;
    advanceToken.current += 1;
  };
  useEffect(() => () => clearTimeout(advanceTimer.current), []);
  const [dismissed, setDismissed] = useState(false);
  const [optionIds, setOptionIds] = useState<Record<string, string[]>>({});
  const [freeText, setFreeText] = useState<Record<string, string>>({});

  const total = survey.questions.length;
  const settled = !!submittedAnswer || dismissed || !onSubmit;

  if (total === 0) return null;

  // In the composer slot there is no settled rendering: the prompts already read back in the
  // transcript row, and the slot belongs to the input again the moment the ask is done with.
  if (inComposer && settled) return null;

  // Settled: the prompts alone, as a list rather than a card. No options to re-show (the picks are on
  // the user's message), so nothing here is interactive and none of the draft state below is read.
  if (settled) {
    const prompts = (
      <ul className={styles.settled} aria-label={t('chat.survey.groupLabel')}>
        {survey.questions.map((q) => (
          <Typography key={q.id} variant="p2" color="secondary" component="li" className={styles.settledPrompt}>
            <span dir={messageDir(q.prompt)}>{q.prompt}</span>
          </Typography>
        ))}
      </ul>
    );
    if (!onReopen) return prompts;
    // Closing the card was the reader saying "not now", not "never" — and until they say anything at
    // all, the questions are still live. So the record doubles as the way back to them: the whole block
    // is one control, captioned, so that it is READ as clickable rather than discovered by hovering.
    // A `div` carrying `role="button"` rather than a real button ELEMENT, because a button element's
    // content model is phrasing content and this control's content is the prompt LIST.
    return (
      <div
        role="button"
        tabIndex={0}
        className={styles.reopen}
        onClick={onReopen}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          onReopen();
        }}
      >
        {prompts}
        <Typography variant="p3" color="primary" component="span" className={styles.reopenHint}>
          {t('chat.survey.reopen')}
        </Typography>
      </div>
    );
  }

  // An "Other" with nothing under it is NOT an answer: `buildSurveyAnswer` drops the synthetic id, so a
  // row left marked would show a question as answered while contributing nothing to what is sent. The tap
  // still does something — it is how a reader says "none of these" on the way to writing — but the moment
  // they leave the question without writing, the mark goes with them. What the card shows and what it
  // sends have to be the same thing, or Submit quietly drops an answer the reader watched themselves give.
  const withoutEmptyOther = (ids: Record<string, string[]>, text: Record<string, string>) => {
    const cleaned: Record<string, string[]> = { ...ids };
    for (const q of survey.questions) {
      if ((text[q.id] ?? '').trim()) continue;
      const picked = cleaned[q.id] ?? [];
      if (picked.includes(OTHER_OPTION_ID)) cleaned[q.id] = picked.filter((id) => id !== OTHER_OPTION_ID);
    }
    return cleaned;
  };

  const safePage = Math.min(page, total - 1);
  // EVERY page the reader turns themselves goes through here — the pager, Next, Skip. Going through
  // one function is what makes "a manual move voids a pending advance" true by construction rather
  // than by each call site remembering.
  const goToPage = (next: number, trigger: SurveyPageTrigger) => {
    cancelAdvance();
    setOptionIds((prev) => withoutEmptyOther(prev, freeText));
    reportPageTurn(safePage, next, trigger);
    setPage(next);
  };
  const reportPageTurn = (from: number, to: number, trigger: SurveyPageTrigger) =>
    report(SURVEY_BI.pageTurned, { from_question: String(from + 1), to_question: String(to + 1), trigger });
  const question = survey.questions[safePage];
  const isLast = safePage === total - 1;

  const dismiss = (reason: SurveyDismissReason, settledDraft?: SurveyDraft) => {
    // Closing the card is an action too, so it voids a pending advance — the rule is "anything else
    // happening voids it", and dismissal was the one path that did not say so. Without this, an X pressed
    // inside the beat after a pick still fired the scheduled turn, so a `page_turned` landed AFTER the
    // `dismissed` event: a funnel in which readers move on from cards they have already closed.
    cancelAdvance();
    // How far the reader had got is the whole value of a dismissal: an ask closed on question one is a
    // card that was never wanted, and one closed on question four is a card that asked too much.
    //
    // Counted off the draft that DECIDED this dismissal, not off component state. The last-page skip
    // reaches here through `submitWithout`, which cleared the skipped question in a draft of its own
    // because setState has not landed yet — so recomputing from the closure would report
    // `nothing_answered` beside a count of the very answer that was just dropped. An event that
    // contradicts itself is worse than no event: whichever half a reader of it trusts, they are wrong
    // half the time.
    report(SURVEY_BI.dismissed, {
      reason,
      on_question: String(safePage + 1),
      ...surveyProgress(survey, settledDraft ?? { optionIds, freeText }),
    });
    setDismissed(true);
    onDismiss?.();
  };

  const submit = () => submitWithout();

  // `skippedId` is the question the reader just skipped: setState has not landed yet when Skip submits
  // from the last page, so the draft in hand still holds that page's picks.
  const submitWithout = (skippedId?: string) => {
    const text = skippedId ? { ...freeText, [skippedId]: '' } : freeText;
    const draft: SurveyDraft = {
      // …and the same rule at the end: an Other nobody wrote under never reaches the answer, so the
      // progress counted on a dismissal cannot disagree with the payload either.
      optionIds: withoutEmptyOther(skippedId ? { ...optionIds, [skippedId]: [] } : optionIds, text),
      freeText: text,
    };
    const answer = buildSurveyAnswer(survey, draft);
    // Nothing answered anywhere across the pages reads as Skip — settle without sending.
    if (!answer) {
      dismiss('nothing_answered', draft);
      return;
    }
    onSubmit?.(answer, composeSurveyMessageText(survey, answer));
  };

  const setQuestionOptions = (values: string[]) => {
    const before = optionIds[question.id] ?? [];
    const added = values.filter((id) => !before.includes(id));
    // On a multiSelect question a tap can also TAKE an answer back, and the two are different facts:
    // one says a suggestion fit, the other says it stopped fitting once the reader saw the rest.
    const changed = added[0] ?? before.find((id) => !values.includes(id));
    if (changed) {
      report(SURVEY_BI.picked, {
        question: questionIndex(survey, question.id),
        option: optionIndex(question, changed),
        // NOT `action`: the click's `additionalFields` are merged UNDER the derived src:172 fields, one
        // of which is `action` — so this arrived as an empty `action` and was dropped on the floor,
        // taking the select/deselect distinction with it.
        pick_action: added.length > 0 ? 'select' : 'deselect',
        multi_select: String(question.multiSelect === true),
        selected_count: String(values.length),
      });
    }
    setOptionIds((prev) => ({ ...prev, [question.id]: values }));
    // Dropping Other clears what was typed under it. The field itself STAYS — it is always on screen
    // now — but its words belonged to the answer the reader has just replaced, and submitting them
    // under a suggested option nobody attached them to would answer something they did not say.
    if (!values.includes(OTHER_OPTION_ID)) {
      setFreeText((prev) => (prev[question.id] ? { ...prev, [question.id]: '' } : prev));
    }
    // A new pick voids the pending one FIRST, before any of the reasons not to advance below — a pick
    // that does not schedule an advance must still cancel the one already in flight. Picking a
    // suggested answer and then changing to Other inside the beat is exactly that case: the old timer
    // would otherwise turn the page and close the field the change had just opened.
    cancelAdvance();
    // Picking an answer IS the page's answer, so the card moves on by itself — a single-select question
    // has nothing left to do once one option is chosen, and making the reader confirm it with Next asks
    // for two taps where the design asks for one.
    //
    // NOT on a multiSelect question: there the first pick is rarely the last, and advancing would cut
    // the reader off mid-answer. And not on the last page — there is nowhere to advance TO, and the
    // remaining move is Submit, which must stay the reader's own.
    if (question.multiSelect || isLast || values.length === 0) return;
    // Picking "Other" is the START of an answer, not the end of one — the field under it is still
    // empty. Turning the page here would take the reader off it before a word was typed.
    if (values.includes(OTHER_OPTION_ID)) return;
    // A beat first, so the radio is SEEN to take the pick rather than the page vanishing under it.
    const token = advanceToken.current;
    advanceTimer.current = setTimeout(() => {
      // Anything at all since this was scheduled — a pick, a page the reader turned, a skip — bumped
      // the token, and a void advance stays void.
      if (advanceToken.current !== token) return;
      setPage((current) => {
        reportPageTurn(current, current + 1, 'auto');
        return current + 1;
      });
    }, AUTO_ADVANCE_MS);
  };

  // Typing in the write-in field IS choosing Other — the field is always on screen now, so it is a way
  // INTO the question rather than something a pick opens, and the reader who uses it never has to also
  // find the row above it. Emptying it again gives the pick back: an Other with nothing under it
  // answers nothing (`buildSurveyAnswer` drops the synthetic id), so leaving the row marked would show
  // a question as answered that is not.
  //
  // It never schedules an advance — the reader is mid-sentence, and a page that turns while they type
  // is the complaint this card already collected once, in its sharpest form.
  const setQuestionText = (value: string) => {
    cancelAdvance();
    if (value.trim() && !wroteIn.current.has(question.id)) {
      wroteIn.current.add(question.id);
      report(SURVEY_BI.wroteIn, { question: questionIndex(survey, question.id) });
    }
    setFreeText((prev) => ({ ...prev, [question.id]: value }));
    const picked = optionIds[question.id] ?? [];
    if (value.trim() && !picked.includes(OTHER_OPTION_ID)) {
      // Single-select: Other REPLACES the pick, exactly as tapping the row would. Multi-select: it
      // joins the rest, because there the answers are meant to add up.
      setOptionIds((prev) => ({ ...prev, [question.id]: question.multiSelect ? [...picked, OTHER_OPTION_ID] : [OTHER_OPTION_ID] }));
      return;
    }
    if (!value.trim() && picked.includes(OTHER_OPTION_ID)) {
      setOptionIds((prev) => ({ ...prev, [question.id]: picked.filter((id) => id !== OTHER_OPTION_ID) }));
    }
  };

  // Skip this QUESTION, not the survey — the X is what dismisses the whole card. The question's draft
  // is cleared on the way out: the reader is saying "not this one", so leaving an earlier pick behind
  // to be submitted under it would answer for them.
  const skipQuestion = () => {
    // A skip is an action too, so it voids a pending advance even on the last page, where it submits
    // rather than turning the page.
    cancelAdvance();
    // `had_draft` is what separates "nothing to say here" from "I had started and changed my mind" —
    // the second is a question that misfired after the reader engaged with it.
    report(SURVEY_BI.skipped, {
      question: questionIndex(survey, question.id),
      had_draft: String((optionIds[question.id]?.length ?? 0) > 0 || (freeText[question.id] ?? '').trim().length > 0),
    });
    setOptionIds((prev) => ({ ...prev, [question.id]: [] }));
    setFreeText((prev) => ({ ...prev, [question.id]: '' }));
    // Nowhere left to skip TO: send what the other pages answered. `submit` already settles the whole
    // survey unanswered when that is nothing at all, so there is still a way out from the last page.
    if (isLast) {
      submitWithout(question.id);
      return;
    }
    goToPage(safePage + 1, 'skip');
  };

  return (
    <Card
      // The impression rides the card's own element: an offer counts when a human could have seen it,
      // which is a viewport fact about this node (use-survey-shown-bi).
      ref={offerSeenRef}
      // The skin is picked by what is UNDERNEATH, per the DS Card rule. In the composer slot the card
      // stands where the composer's white pill stood, on the grey chat surface — so it is `sunken`, the
      // DS answer for a white surface on the page ground; `outline` (a transparent fill) would read as
      // the composer having vanished and some text floated up in its place. Inline in the transcript it
      // sits ON the chat surface as one more block of the turn, and stays the quiet outline.
      variant={inComposer ? 'sunken' : 'outline'}
      className={inComposer ? `${styles.card} ${styles.composerCard}` : styles.card}
      component="section"
      aria-label={t('chat.survey.groupLabel')}
    >
      <div className={styles.header}>
        <Typography variant="h3" color="strong" component="h3" className={styles.prompt}>
          <span dir={messageDir(question.prompt)}>{question.prompt}</span>
        </Typography>
        <IconButton size="small" variant="text" aria-label={t('common.close')} onClick={() => dismiss('closed')} className={styles.close}>
          <X size={16} aria-hidden />
        </IconButton>
      </div>

      <QuestionBody
        question={question}
        selected={optionIds[question.id] ?? []}
        text={freeText[question.id] ?? ''}
        otherLabel={t('chat.survey.otherOption')}
        freeTextPlaceholder={t('chat.survey.freeTextPlaceholder')}
        onSelect={setQuestionOptions}
        onText={setQuestionText}
      />

      <div className={styles.footer}>
        {/* A one-question survey has nothing to page through: "1/1" between two chevrons that can never
            move is a control that lies about there being more to answer. The card is a single page, so
            it wears no pager — `.actions` right-aligns itself either way. */}
        {total > 1 && (
          <div className={styles.pagination}>
            <IconButton
              size="tiny"
              variant="text"
              aria-label={t('chat.survey.previousQuestion')}
              disabled={safePage === 0}
              onClick={() => goToPage(safePage - 1, 'previous')}
            >
              <ChevronLeft size={16} aria-hidden className="dir-flip" />
            </IconButton>
            <Typography variant="p3" color="secondary" component="span">
              {t('chat.survey.pagination', { current: safePage + 1, total })}
            </Typography>
            <IconButton
              size="tiny"
              variant="text"
              aria-label={t('chat.survey.nextQuestion')}
              disabled={isLast}
              onClick={() => goToPage(safePage + 1, 'next')}
            >
              <ChevronRight size={16} aria-hidden className="dir-flip" />
            </IconButton>
          </div>
        )}
        <div className={styles.actions}>
          <Button variant="text" size="small" onClick={skipQuestion}>
            {t('chat.survey.skip')}
          </Button>
          <Button size="small" onClick={isLast ? submit : () => goToPage(safePage + 1, 'next')}>
            {isLast ? t('chat.survey.submit') : t('chat.survey.next')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function QuestionBody({
  question,
  selected,
  text,
  otherLabel,
  freeTextPlaceholder,
  onSelect,
  onText,
}: {
  question: SurveyQuestion;
  selected: string[];
  text: string;
  otherLabel: string;
  freeTextPlaceholder: string;
  onSelect: (values: string[]) => void;
  onText: (value: string) => void;
}) {
  // "Other" is the LAST answer on every question — an answer rather than a field sitting beside the
  // answers — and its field is ALWAYS on screen, not opened by picking the row.
  //
  // It used to open on the pick, on the argument that an always-open box competes with the options and
  // asks the reader to write where one tap would do. The review answered that from the other side: a
  // field you have to discover is a field most readers never find, and the one question the card most
  // needs answered is the one none of the suggestions fit. An always-visible field costs the options a
  // little attention; a hidden one costs the answer entirely. So it shows, and typing in it is itself
  // the pick (see `setQuestionText`) — the row above it is a shortcut for the reader who wants to say
  // "none of these" without writing, not a gate in front of the field.
  // A question whose every answer has a picture is drawn as a grid of tiles: the picture over the label. The group, its
  // inputs and its selection rules are the same as the rows', so only the layout changes.
  const tiles = showsPictures(question);
  const options = [
    ...question.options.map((o) => ({
      value: o.id,
      label: tiles ? (
        <span className={styles.tileLabel}>
          <SurveyPicture src={o.imageUrl!} alt={o.imageAlt ?? ''} />
          <span className={styles.tileTitle} dir={messageDir(o.label)}>
            {o.label}
          </span>
        </span>
      ) : (
        <span dir={messageDir(o.label)}>{o.label}</span>
      ),
      ...(o.description ? { description: <span dir={messageDir(o.description)}>{o.description}</span> } : {}),
    })),
    {
      value: OTHER_OPTION_ID,
      label: <span dir="auto">{otherLabel}</span>,
      // The field rides the option's own DESCRIPTION slot, so it renders INSIDE the row's text column —
      // under the label, sharing the row's fill and its left edge. A field placed after the group is a
      // separate control that happens to sit nearby; this one is visibly part of the answer it belongs
      // to, which is what makes "Other" read as one thing to fill in rather than two to notice.
      description: (
        // The row is a <label>, so a click inside it would otherwise be forwarded to the hidden radio
        // and toggle the option the reader is trying to type into.
        <span className={styles.otherField} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <Input
            size="medium"
            value={text}
            onChange={(e) => onText(e.target.value)}
            placeholder={freeTextPlaceholder}
            aria-label={freeTextPlaceholder}
            // NOT autofocused: the field is on screen for every question now, so focusing it would put
            // the caret in a text box on every page turn — on a card whose first answer is a tap, and
            // on a phone that means the keyboard covering the options the reader came to read.
          />
        </span>
      ),
    },
  ];

  return (
    <ScrollArea key={question.id} className={styles.body}>
      {question.multiSelect ? (
        <CheckboxGroup
          options={options}
          value={selected}
          onChange={onSelect}
          variant="card"
          aria-label={question.prompt}
          className={tiles ? styles.tileList : undefined}
          itemClassName={tiles ? styles.tile : undefined}
        />
      ) : (
        <RadioGroup
          options={options}
          value={selected[0] ?? null}
          onChange={(value) => onSelect([value])}
          variant="card"
          aria-label={question.prompt}
          className={tiles ? styles.tileList : undefined}
          itemClassName={tiles ? styles.tile : undefined}
        />
      )}
    </ScrollArea>
  );
}

/** A tile's picture, fitted into a square. A picture that fails to load leaves the square empty; the label still says it. */
function SurveyPicture({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  return <span className={styles.tilePicture}>{!failed && <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />}</span>;
}
